<script lang="ts">
    import type {MenuItem} from 'shared/components/kp-main-menu/types';
    import type {DefinedAction, GoToAction} from 'shared/defined-actions/types';
    import {createFloatingActions} from 'svelte-floating-ui';
    import {clickOutside} from 'shared/svelte-actions/use.click-outside';
    import {exists, uuid} from 'shared/utils/custom-utils';
    import {slide, fade} from 'svelte/transition';
    import {offset} from 'svelte-floating-ui/dom';
    import {autoPlacement} from '@floating-ui/core';
    import {tooltip} from 'shared/ui-widgets/tooltip/use.tooltip';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';

    export let menuCollapsed = false;
    export let menuItem: MenuItem;

    const itemUuid = uuid();
    const [floatingRef, floatingContent] = createFloatingActions({
        placement: 'right-start' as const,
        strategy: 'fixed' as const,
        middleware: [
            offset(20),
            autoPlacement({
                allowedPlacements: ['right-start', 'right', 'right-end'],
            })
        ]
    });

    $: hasSuboptions = exists(menuItem.submenu) && menuItem.submenu.length > 0;
    let suboptionsOpen = false;
    $: if (menuCollapsed) closeSuboptions();

    const handleClick = () => {
        if (hasSuboptions) {
            suboptionsOpen = !suboptionsOpen;
            return;
        }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            suboptionsOpen = false;
        }
    }

    function closeSuboptions() {
        suboptionsOpen = false;
    }

    function isPathAction(action: DefinedAction | undefined): action is GoToAction {
        if (!exists(action)) {
            return false;
        }

        return 'path' in action && exists(action.path);
    }
</script>

<li class="erp-sidebar-nav-item-wrapper">
    <svelte:element
            this="{isPathAction(menuItem.action) && (!exists(menuItem.submenu) || suboptionsOpen) ? 'a' : 'button'}"
            role="menuitem"
            tabindex="0"
            class="erp-sidebar-nav-item nav-item-{itemUuid}"
            use:floatingRef
            class:suboptions-open={suboptionsOpen}
            class:menu-collapsed={menuCollapsed}
            on:click={handleClick}
            use:tooltip={{enabled: menuCollapsed, content: menuItem.text, role: 'tooltip'}}
            href="{isPathAction(menuItem.action) ? menuItem.action.path : undefined}">

        {#if exists(menuItem.iconName)}
            <UIcon icon="{menuItem.iconName}"/>
        {/if}

        <!-- Placeholder question mark icon -->
        {#if !exists(menuItem.iconName) && menuCollapsed}
            <UIcon icon="question"/>
        {/if}

        {#if !menuCollapsed}
            <span class="nav-item-content">
                <slot/>

                {#if hasSuboptions}
                    <div class="suboptions-open-icon" class:opened={suboptionsOpen}>
                        <UIcon icon="angle-small-right"/>
                    </div>
                {/if}
            </span>
        {/if}

        {#if menuCollapsed && hasSuboptions}
            <div class="collapsed-nav-item-content">
                <UIcon icon="angle-small-right"/>
            </div>
        {/if}

        {#if menuCollapsed && !hasSuboptions && exists($$slots['collapsed-content'])}
            <div class="collapsed-nav-item-content">
                <slot name="collapsed-content"/>
            </div>
        {/if}
    </svelte:element>

    {#if suboptionsOpen && !menuCollapsed}
        <ul class="suboptions-list" transition:slide={{duration: 250}}>
            {#each menuItem.submenu as subMenuItem}
                <svelte:self menuItem="{subMenuItem}">
                    {subMenuItem.text}
                </svelte:self>
            {/each}
        </ul>
    {/if}

    {#if suboptionsOpen && menuCollapsed}
        <ul class="suboptions-list suboptions-list-floating"
            role="menu"
            use:clickOutside={{ignoredElementsSelectors: [`.nav-item-${itemUuid}`]}}
            use:floatingContent
            in:fade={{duration: 250}}
            on:click={() => suboptionsOpen = false}
            on:keydown={handleKeyDown}
            on:click-outside={() => suboptionsOpen = false}>

            {#each menuItem.submenu as subMenuItem}
                <svelte:self menuItem="{subMenuItem}">
                    {subMenuItem.text}
                </svelte:self>
            {/each}
        </ul>
    {/if}
</li>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    @nav-menu-item-height: 32px;
    @collapsed-nav-menu-item-size: 42px;

    .erp-sidebar-nav-item-wrapper {
        display: flex;
        flex-direction: column;

        .erp-sidebar-nav-item {
            position: relative;
            display: flex;
            align-items: center;
            height: @nav-menu-item-height;
            width: 100%;
            gap: @spacing-ml;
            color: @themed-text-muted;
            outline: none;
            border: none;
            cursor: pointer;
            background: none;
            text-decoration: none;
            padding: 0 @spacing-m;

            &:before {
                content: '';
                position: absolute;
                z-index: -1;
                left: 0;
                right: 0;
                top: 0;
                bottom: 0;
                border-radius: @border-radius-default;
                background-color: transparent;
                transition: background-color 0.2s ease-in-out;
            }

            &:hover {
                text-decoration: none;
                color: @themed-text-default;

                &:before {
                    background-color: @themed-body-bg-blue-highlighted;
                }
            }

            &.menu-collapsed {
                padding: 0;
                justify-content: center;
                width: @collapsed-nav-menu-item-size;
                height: @collapsed-nav-menu-item-size;
                align-items: center;
            }

            .nav-item-content {
                display: flex;
                flex: 1;
                gap: @spacing-m;
                align-items: center;
                overflow: hidden;
                white-space: nowrap;
                justify-content: space-between;
            }

            .collapsed-nav-item-content {
                position: absolute;
                top: 50%;
                right: 0;
                transform: translate(3px, -50%);
            }

            .suboptions-open-icon {
                transition: rotate 0.2s ease-in-out;

                &.opened {
                    rotate: -90deg;
                }
            }
        }

        .suboptions-list {
            display: flex;
            flex-direction: column;
            gap: @spacing-m;
            margin-top: @spacing-ml;
            padding-left: @spacing-sm;
            margin-left: @spacing-l;
            border-left: 1px solid var(--accent-blue-new);
        }

        .suboptions-list-floating {
            width: min-content;
            padding: @spacing-sm @spacing-m @spacing-sm @spacing-sm;
            background-color: @themed-body-bg;
            border-radius: @border-radius-default;
            border: 1px solid @themed-border-default;
            margin: 0;
        }
    }
</style>